<template>
    <page :tabs="tabs" :busy="busy">
        <b-button-toolbar class="button-toolbar-toolbar m-1 ml-2">
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDateRangeChange"
            ></date-range-picker>
            <responsive-button
                :icon="['fas', 'gear']"
                @click="$bvModal.show(settingsModalId)"
                variant="primary"
                >Settings</responsive-button
            >
            <div class="d-none d-sm-flex align-items-baseline mr-1">
                <owners-search-multiselect
                    v-model="filterOwners"
                    variant="primary"
                    :owners="filterOwnerOptions"
                />
            </div>
            <div class="d-xl-block d-none">
                <responsive-dropdown
                    :icon="['fas', 'filter']"
                    :items="filterByOptions"
                    :responsiveness="false"
                    variant="primary"
                >
                    {{ filterByLabel }}
                </responsive-dropdown>
            </div>
            <form-square-switch
                class="d-xl-block d-none save-to-owner-account-toggle mr-1"
                v-model="saveToOwnerAccount"
                :labels="{
                    checked: 'Save To Owner Account',
                    unchecked: 'Save To Owner Account',
                }"
                :isToolbar="true"
                :sync="true"
            />
            <div class="d-none d-md-flex align-items-baseline">
                <responsive-dropdown
                    :items="filterReportTypeOptions"
                    :responsiveness="false"
                    variant="primary"
                    :tooltip="
                        !canChangeReportType
                            ? 'You must select all owners to be able to change the report type.'
                            : null
                    "
                    tooltipVariant="warning"
                    :disabled="!canChangeReportType"
                >
                    {{ filterReportTypeLabel }}
                </responsive-dropdown>
            </div>
            <b-button-group class="mr-1">
                <more-filters-dropdown
                    :icon="['fas', 'download']"
                    :busy="busy || excelBusy || pdfBusy"
                >
                        <export-to-excel-button
                            class="actions-item"
                            :href="exportReportHref(excel)"
                            :displayLabel="true"
                            :label="'Download Excel'"
                            :disabled="!canGenerateReport"
                            :responsiveness="false"
                            @busy="handleExcelBusy"
                        />
                        <export-to-pdf-button
                            class="actions-item mb-3"
                            :href="exportReportHref(pdf)"
                            :displayLabel="true"
                            :label="'Download PDF'"
                            :disabled="!canGenerateReport"
                            :responsiveness="false"
                            @busy="handlePDFBusy"
                        />
                        <responsive-button
                            class="actions-item"
                            variant="primary"
                            :icon="['fas', 'envelope']"
                            :responsiveness="false"
                            :busy="busy"
                            :disabled="
                                !canGenerateReport ||
                                filterReportType !== 'DOWNLOAD'
                            "
                            @click="sendToOwners(excel)"
                        >
                            Email Excel to Owners
                        </responsive-button>
                        <responsive-button
                            class="actions-item"
                            variant="primary"
                            :icon="['fas', 'envelope']"
                            :responsiveness="false"
                            :busy="busy"
                            :disabled="
                                !canGenerateReport ||
                                filterReportType !== 'DOWNLOAD'
                            "
                            @click="sendToOwners(pdf)"
                        >
                            Email PDF to Owners
                        </responsive-button>
                </more-filters-dropdown>
            </b-button-group>
            <b-button-group class="d-flex">
                <more-filters-dropdown class="more-filters-dropdown">
                    <template
                        v-slot:default="{
                            handleDropdownShow,
                            handleDropdownHide,
                        }"
                    >
                        <owners-search-multiselect
                            class="d-sm-none d-flex"
                            v-model="filterOwners"
                            variant="primary"
                            :owners="filterOwnerOptions"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        />
                        <responsive-dropdown
                            class="d-flex d-xl-none"
                            :icon="['fas', 'filter']"
                            :items="filterByOptions"
                            variant="primary"
                            :responsiveness="false"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        >
                            {{ filterByLabel }}
                        </responsive-dropdown>
                        <responsive-dropdown
                            class="d-md-none d-flex"
                            :items="filterReportTypeOptions"
                            variant="primary"
                            :responsiveness="false"
                            :tooltip="
                                !canChangeReportType
                                    ? 'You must select all owners to be able to change the report type.'
                                    : null
                            "
                            tooltipVariant="warning"
                            :disabled="!canChangeReportType"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        >
                            {{ filterReportTypeLabel }}
                        </responsive-dropdown>
                        <form-square-switch
                            class="w-100 d-xl-none save-to-owner-account-toggle"
                            v-model="saveToOwnerAccount"
                            :labels="{
                                checked: 'Save To Owner Account',
                                unchecked: 'Save To Owner Account',
                            }"
                            :sync="true"
                        />
                        <report-settings-modal
                            :modalId="reportSettingsModalId"
                            :initialValues="reportSettings"
                            class="mt-1 w-100 report-settings-button"
                            label="Report Settings"
                            :responsiveness="false"
                            :disabled="!canGenerateReport"
                        />
                    </template>
                </more-filters-dropdown>
            </b-button-group>
        </b-button-toolbar>
        <settings-modal-form :modal-id="settingsModalId" :settings="settings" />
    </page>
</template>

<script>
import format from 'date-fns/format';
import API from '../../../api';
import Page from '../../generic/page/page.vue';
import { mapGetters } from 'vuex';
import Modal from '../../generic/modal/modal.vue';
import OwnersSearchMultiselect from './owners-search-multiselect.vue';
import FormSearchMultiselect from '../../generic/form-search-multiselect/form-search-multiselect.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
import FormSquareSwitch from '../../generic/form-square-switch/form-square-switch.vue';
import ExportToExcelButton from '../../generic/export-to-excel-button/export-to-excel-button.vue';
import ExportToPdfButton from '../../generic/export-to-pdf-button/export-to-pdf-button.vue';
import SettingsModalForm from './settings-modal-form.vue';
import DateRangePicker, {
    LAST_MONTH,
    LAST_WEEK,
    LAST_YEAR,
    nameToRange,
    THIS_MONTH,
    THIS_WEEK,
    THIS_YEAR,
    TODAY,
    YESTERDAY,
    ALL_DATES,
    CUSTOM_RANGE,
} from '../../generic/date-range-picker/date-range-picker.vue';
import {
    REPORT_OWNER_STATEMENT_REPORT_ENDPOINT,
    REPORT_OWNER_STATEMENT_OWNERS_ENDPOINT,
    REPORT_OWNER_STATEMENT_SETTINGS_ENDPOINT,
} from '../../../endpoints';
import ReportSettingsModal, {
    DATE_PERIODS_INPUT,
    SELECT_INPUT,
    OWNER_SELECT_INPUT,
} from '../report-settings/report-settings-modal.vue';

export default {
    name: 'OwnerStatementReport',
    components: {
        Page,
        Modal,
        DateRangePicker,
        FormSquareSwitch,
        FormSearchMultiselect,
        ResponsiveButton,
        ResponsiveDropdown,
        MoreFiltersDropdown,
        SettingsModalForm,
        ExportToPdfButton,
        ExportToExcelButton,
        OwnersSearchMultiselect,
        ReportSettingsModal,
    },
    data() {
        const defaultSaveToOwnerAccount =
            this.$store.getters[
                'ownerStatementLocalStorage/saveToOwnerAccount'
            ];
        const defaultFilterBy =
            this.$store.getters['ownerStatementLocalStorage/filterBy'];
        const defaultReportType =
            this.$store.getters['ownerStatementLocalStorage/reportType'];
        return {
            busy: false,
            sending: false,
            excelBusy: false,
            pdfBusy: false,
            title: 'Owner Statement Report',
            tabs: [{ title: 'Owner Statement Report', active: true }],
            dateRange: nameToRange(LAST_MONTH),
            dateRequestFormat: 'yyyy-MM-dd',
            predefinedRanges: [
                TODAY,
                YESTERDAY,
                THIS_WEEK,
                LAST_WEEK,
                THIS_MONTH,
                LAST_MONTH,
                THIS_YEAR,
                LAST_YEAR,
                ALL_DATES,
                CUSTOM_RANGE,
            ],
            settings: null,
            filterOwnerOptions: [],
            filterOwnerOptionIds: new Set(),
            filterBy: defaultFilterBy,
            filterReportType: defaultReportType,
            saveToOwnerAccount: defaultSaveToOwnerAccount,
            settingsModalId: 'settings-modal',
            excel: 'excel',
            pdf: 'pdf',
            reportSettingsModalId: 'report-settings-modal',
        };
    },
    computed: {
        ...mapGetters('header', [
            'userUnicodeDateFormat',
            'userCurrencySymbol',
        ]),
        ownersRequest() {
            return this.filterOwners.join(',');
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        canGenerateReport() {
            return this.filterOwners?.length > 0;
        },
        canChangeReportType() {
            return this.filterOwners?.length == this.filterOwnerOptions?.length;
        },
        filterByLabel() {
            switch (this.filterBy) {
                case 'ALL_TAX':
                    return 'Show All Tax Collected';
                case 'ONLY_TAX_ON_RENTAL_CHARGES':
                    return 'Show Only Tax on Rental Charges';
            }
        },
        filterReportTypeLabel() {
            switch (this.filterReportType) {
                case 'DOWNLOAD':
                    return 'Individual Statements';
                case 'SUMMARY':
                    return 'Summary Statement';
                case 'IRS1099':
                    return 'IRS 1099';
            }
        },
        filterOwners: {
            get() {
                const defaultOwners =
                    this.$store.getters['ownerStatementLocalStorage/owners'];
                return defaultOwners.filter((ownerId) =>
                    this.filterOwnerOptionIds.has(ownerId)
                );
            },
            set(owners) {
                this.$store.dispatch(
                    'ownerStatementLocalStorage/changeOwners',
                    owners
                );
            },
        },
        filterByOptions() {
            return [
                {
                    label: 'Show All Tax Collected',
                    active: this.filterBy === 'ALL_TAX',
                    click: () => (this.filterBy = 'ALL_TAX'),
                },
                {
                    label: 'Show Only Tax on Rental Charges',
                    active: this.filterBy === 'ONLY_TAX_ON_RENTAL_CHARGES',
                    click: () => (this.filterBy = 'ONLY_TAX_ON_RENTAL_CHARGES'),
                },
            ];
        },
        filterReportTypeOptions() {
            return [
                {
                    label: 'Individual Statements',
                    active: this.filterReportType === 'DOWNLOAD',
                    click: () => (this.filterReportType = 'DOWNLOAD'),
                },
                {
                    label: 'Summary Statement',
                    active: this.filterReportType === 'SUMMARY',
                    click: () => (this.filterReportType = 'SUMMARY'),
                },
                {
                    label: 'IRS 1099',
                    active: this.filterReportType === 'IRS1099',
                    click: () => (this.filterReportType = 'IRS1099'),
                },
            ];
        },
        reportSettings() {
            return {
                name: 'Owner Statement',
                report_sysname: 'OWNER_STATEMENTS',
                settings: [
                    {
                        type: DATE_PERIODS_INPUT,
                        value: 'this_month',
                        predefinedRanges: this.predefinedRanges,
                        label: 'Period',
                        name: 'from_date',
                    },
                    {
                        type: SELECT_INPUT,
                        value: 'pdf',
                        label: 'Report Format',
                        name: 'report_format',
                        options: [
                            {
                                text: 'PDF',
                                value: this.pdf,
                            },
                            {
                                text: 'Excel',
                                value: this.excel,
                            },
                        ],
                    },
                    {
                        type: 'string',
                        value: this.filterBy,
                        label: 'Filter By',
                        name: 'filter_by',
                    },
                    {
                        type: 'string',
                        value: this.ownersRequest,
                        label: 'Owners',
                        name: 'owner_ids',
                    },

                    {
                        type: 'string',
                        value: this.sending ? 'SEND' : this.filterReportType,
                        label: 'Report Type',
                        name: 'report_type',
                    },
                    {
                        type: '',
                        value: false,
                        name: 'save_to_owner_account',
                    },
                ],
            };
        },
    },
    mounted() {
        Promise.all([this.fetchOwners(), this.fetchSettings()]);
    },
    methods: {
        async fetchOwners() {
            try {
                const { data } = await API.get(
                    REPORT_OWNER_STATEMENT_OWNERS_ENDPOINT()
                );
                this.filterOwnerOptions = data;
                this.filterOwnerOptionIds = new Set(
                    data.map((option) => option.id)
                );
            } catch (e) {
                this.errorToast({ text: 'Could not fetch owners.' });
            }
        },
        async fetchSettings() {
            try {
                const { data } = await API.get(
                    REPORT_OWNER_STATEMENT_SETTINGS_ENDPOINT()
                );
                this.settings = data;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch settings.' });
            }
        },
        async sendToOwners(reportFormat) {
            try {
                this.busy = true;
                this.sending = true;
                await API.get(this.exportReportHref(reportFormat));
                this.successToast({
                    text: 'Reports successfully sent to owners.',
                });
            } catch (e) {
                this.errorToast({ text: 'Could not send reports to owners.' });
            } finally {
                this.busy = false;
                this.sending = false;
            }
        },
        exportReportHref(reportFormat) {
            let url = REPORT_OWNER_STATEMENT_REPORT_ENDPOINT();
            url += `?from_date=${this.startDateRequest}`;
            url += `&to_date=${this.endDateRequest}`;
            url += `&owner_ids=${this.ownersRequest}`;
            url += `&filter_by=${this.filterBy}`;
            url += `&report_format=${reportFormat}`;
            url += `&report_type=${
                this.sending ? 'SEND' : this.filterReportType
            }`;
            url += `&save_to_owner_account=${this.saveToOwnerAccount}`;
            return url;
        },
        handleDateRangeChange(value) {
            this.dateRange = { ...value };
        },
        handleExcelBusy(busy) {
            this.excelBusy = busy;
        },
        handlePDFBusy(busy) {
            this.pdfBusy = busy;
        },
    },
    watch: {
        saveToOwnerAccount(saveToOwnerAccount) {
            this.$store.dispatch(
                'ownerStatementLocalStorage/changeSaveToOwnerAccount',
                saveToOwnerAccount
            );
        },
        filterBy(filterBy) {
            this.$store.dispatch(
                'ownerStatementLocalStorage/changeFilterBy',
                filterBy
            );
        },
        filterReportType(reportType) {
            this.$store.dispatch(
                'ownerStatementLocalStorage/changeReportType',
                reportType
            );
        },
        canChangeReportType(canChangeReportType) {
            if (!canChangeReportType) {
                this.filterReportType = 'DOWNLOAD';
                this.$store.dispatch(
                    'ownerStatementLocalStorage/changeReportType',
                    this.filterReportType
                );
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

.button-toolbar > :deep(*) {
    display: flex;
    flex-wrap: wrap-reverse !important;
    justify-content: space-between !important;
    margin-bottom: 5px;
}

.save-to-owner-account-toggle {
    width: 191px;
    @include media-breakpoint-down(lg) {
        width: 217px;
    }
}

.more-filters-dropdown {
    :deep(.buttons-wrapper) {
        width: 100%;
    }
}

.actions-item {
    text-align: left !important;
    width: 210px !important;
}
.report-settings-button {
    min-width: 160px;
}
</style>
